import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

interface BrowserRun {
  id: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  portal_vendor: string | null;
  created_at: string;
  found_report: boolean;
  report_ready: boolean;
  error_message: string | null;
}

interface RunListProps {
  runs: BrowserRun[];
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
}

const StatusIcon = ({ status }: { status: BrowserRun['status'] }) => {
  const config = {
    scheduled: { icon: ClockIcon, className: 'text-gray-500' },
    in_progress: { icon: ClockIcon, className: 'text-blue-600 animate-spin' },
    completed: { icon: CheckCircleIcon, className: 'text-green-600' },
    failed: { icon: XCircleIcon, className: 'text-red-600' },
    cancelled: { icon: ExclamationCircleIcon, className: 'text-gray-500' },
  };

  const { icon: Icon, className } = config[status];
  return <Icon className={`h-4 w-4 ${className}`} />;
};

export const RunList = ({ runs, selectedRunId, onSelectRun }: RunListProps) => {
  return (
    <div className="w-72 border-r border-gray-200 bg-gray-50 overflow-y-auto">
      <div className="p-3 border-b border-gray-200 bg-white">
        <div className="text-sm font-semibold text-gray-900">
          Automation Runs
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{runs.length} total</div>
      </div>
      <div className="divide-y divide-gray-200">
        {runs.map((run) => (
          <button
            key={run.id}
            type="button"
            onClick={() => onSelectRun(run.id)}
            className={`w-full p-3 text-left transition-colors ${
              selectedRunId === run.id
                ? 'bg-blue-50 border-l-4 border-blue-600'
                : 'hover:bg-gray-100 border-l-4 border-transparent'
            }`}
          >
            <div className="flex items-start gap-2">
              <StatusIcon status={run.status} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-900 capitalize">
                    {run.status.replace('_', ' ')}
                  </span>
                  {run.portal_vendor && (
                    <span className="text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded font-mono">
                      {run.portal_vendor}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(run.created_at).toLocaleString()}
                </div>
                {run.status === 'completed' && (
                  <div className="flex items-center gap-2 mt-1 text-xs">
                    <span
                      className={
                        run.found_report ? 'text-green-600' : 'text-gray-400'
                      }
                    >
                      {run.found_report ? '✓' : '✗'} Found
                    </span>
                    <span
                      className={
                        run.report_ready ? 'text-green-600' : 'text-gray-400'
                      }
                    >
                      {run.report_ready ? '✓' : '✗'} Ready
                    </span>
                  </div>
                )}
                {run.error_message && (
                  <div className="text-xs text-red-600 mt-1 truncate">
                    {run.error_message}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
